import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { from } from 'rxjs';
import { concatMap, delay, exhaustMap, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';

import { ChangeAgentDialogComponent } from '@agents/components/change-agent-dialog/change-agent-dialog.component';
import { AppConfigurationService } from '@app-config/services/app-configuration.service';
import * as appointmentsActions from '@appointments/store/actions/appointments.actions';
import { TokenInitializerService } from '@auth/services/token-initializer.service';
import * as loginActions from '@auth/store/actions/login.actions';
import { AuthApiService } from '@auth/store/services/auth-api.service';
import { UserStoreService } from '@auth/store/services/user-store.service';
import * as commentsActions from '@comments/store/actions/comments.actions';
import { RpcRoute } from '@core-layout/app/models/rpc-route';
import { RouteService } from '@core-layout/app/services/route.service';
import { ToastService } from '@core-services/toast.service';
import { INITIAL_DEFAULT_FOLDER_ID } from '@folders/constants/folder.constants';
import * as myListingsActions from '@my-listings/store/actions/my-listings.actions';
import * as notificationsActions from '@notifications/store/actions/notifications.actions';
import * as settingsActions from '@settings/store/actions/settings.actions';
import { SettingsStoreService } from '@settings/store/services/settings-store.service';
import * as switchCollaborationSpaceActions from '../actions/switch-collaboration-space.actions';
import { CollaborationSpaceStoreService } from '../services/collaboration-space-store.service';
import { GoogleAnalyticsStoreService } from '@core-layout/app/store/services/google-analytics-store.service';
import { GoogleAnalyticsEventName } from 'app/modules/core-modules/enums/google-analytics-event-name';

@Injectable()
export class SwitchCollaborationSpaceEffects {

    private readonly initializeNotificationDelay = 1000;

    constructor(
        private readonly actions$: Actions,
        private readonly authApiService: AuthApiService,
        private readonly configurationService: AppConfigurationService,
        private readonly routeService: RouteService,
        private readonly userStoreService: UserStoreService,
        private readonly toaster: ToastService,
        private readonly tokenInitializerService: TokenInitializerService,
        private readonly settingsStoreService: SettingsStoreService,
        private readonly collaborationSpaceStoreService: CollaborationSpaceStoreService,
        private readonly matDialog: MatDialog,
        private readonly googleAnalyticsStoreService: GoogleAnalyticsStoreService,
    ) { }

    public readonly loadCollaborationSpaces$ = createEffect(() =>
        this.actions$.pipe(
            ofType(switchCollaborationSpaceActions.loadCollaborationSpaces),
            switchMap(() => this.authApiService.loadCollaborationSpaces()),
        )
    );

    public readonly loadCollaborationSpacesSuccessful$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(switchCollaborationSpaceActions.loadCollaborationSpacesSuccessful),
                concatLatestFrom(() => this.collaborationSpaceStoreService.collaborationSpaces$),
                filter(([, collaborationSpaces]) => collaborationSpaces.length > 1),
                tap(([, collaborationSpaces]) => {
                    const dialogConfig: MatDialogConfig = { data: collaborationSpaces, autoFocus: false, restoreFocus: false, panelClass: 'ca-dialog' };

                    this.matDialog.open(ChangeAgentDialogComponent, dialogConfig);
                })
            ),
        { dispatch: false }
    );

    public readonly loadHasMultipleCollaborationSpaces$ = createEffect(() =>
        this.actions$.pipe(
            ofType(switchCollaborationSpaceActions.loadHasMultipleCollaborationSpaces),
            switchMap(() => this.authApiService.loadHasMultipleCollaborationSpaces()),
        )
    );

    public readonly switchCollaborationSpaceSuccessful$ = createEffect(() => this.actions$.pipe(
        ofType(switchCollaborationSpaceActions.switchCollaborationSpaceSuccessful),
        switchMap(({ loginResponse, selectedCustomerId }) =>
            from(this.routeService.navigate(RpcRoute.Portfolio, { state: { processDefaultFolder: false } })).pipe(
                tap(() => this.configurationService.configuration = { layout: { spinner: { hidden: false } } }),
                exhaustMap(() => [
                    loginActions.logout(false, false, false),
                    loginActions.collaborationSpaceLogin({ response: loginResponse }),
                    switchCollaborationSpaceActions.resetState({ selectedCustomerId }),
                    switchCollaborationSpaceActions.initializeNotifications(),
                    switchCollaborationSpaceActions.initializeMyPortfolio(),
                ]),
                tap(() => this.configurationService.configuration = { layout: { spinner: { hidden: true } } })
            )
        ))
    );

    public readonly switchCollaborationSpaceFailed$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(switchCollaborationSpaceActions.switchCollaborationSpaceFailed),
                tap((error) => {
                    this.configurationService.configuration = { layout: { spinner: { hidden: true } } };
                    this.toaster.showServerError(error);
                })
            ),
        { dispatch: false }
    );

    public readonly switchCollaborationSpace$ = createEffect(() =>
        this.actions$.pipe(
            ofType(switchCollaborationSpaceActions.switchCollaborationSpace),
            tap(() => {
                this.configurationService.configuration = { layout: { spinner: { hidden: false } } };
                this.googleAnalyticsStoreService.addEvent(GoogleAnalyticsEventName.ChangeAgent);
            }),
            switchMap(({ selectedCustomerId }) => this.authApiService.getCollaborationSpaceToken(selectedCustomerId))
        )
    );

    public readonly initializeNotifications$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(switchCollaborationSpaceActions.initializeNotifications),
                delay(this.initializeNotificationDelay),
                tap(() => this.tokenInitializerService.initializeNotifications())
            ),
        { dispatch: false }
    );

    public readonly resetStates$ = createEffect(
        () => this.actions$.pipe(
            ofType(switchCollaborationSpaceActions.resetState),
            switchMap(({ selectedCustomerId }) => this.userStoreService.customerId$.pipe(
                filter((newCustomerId) => newCustomerId != null && newCustomerId === selectedCustomerId),
                take(1)
            )),
            concatMap(() => [
                notificationsActions.resetState(),
                appointmentsActions.resetState(),
                commentsActions.resetState(),
                myListingsActions.resetState(),
                settingsActions.resetState()
            ])
        )
    );

    public readonly initializeMyPortfolio$ = createEffect(() => this.actions$.pipe(
        ofType(switchCollaborationSpaceActions.initializeMyPortfolio),
        switchMap(() => this.settingsStoreService.defaultFolderId$.pipe(
            filter(defaultFolderId => defaultFolderId !== INITIAL_DEFAULT_FOLDER_ID),
            take(1),
            tap((defaultFolderId) => {
                if (defaultFolderId != null) {
                    this.routeService.navigate(RpcRoute.FolderFullPath, null, null, defaultFolderId.toString()).catch(() => { });
                }
            }),
            map(() => myListingsActions.load())
        ))
    ));
}